import React, { useState } from 'react';
import { CheckCircle, Globe, Shield, Code, Server, Database, ArrowRight, Clock, X } from 'lucide-react';

// --- SUCCESS POPUP COMPONENT ---
const SuccessPopup = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex justify-center items-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center shadow-2xl relative transform transition-all scale-100">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
          <CheckCircle size={40} className="text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h3>
        <p className="text-gray-600 mb-6 text-sm leading-relaxed">
          Thank you for reaching out. We have received your details and sent a confirmation email to you.
        </p>
        <button 
          onClick={onClose} 
          className="bg-red-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 transition w-full shadow-lg"
        >
          Awesome, Close it
        </button>
      </div>
    </div>
  );
};

const WithWeb: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false); // State for new popup
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    phone: '',
    email: '',
    projectBrief: ''
  });

  const openModal = () => setIsQuoteModalOpen(true);
  const closeModal = () => setIsQuoteModalOpen(false);

  // Handle Input Change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- FAST SUBMIT LOGIC ---
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
        ...formData,
        serviceType: "Web Development"
    };

    // 1. Fire Request in Background (Don't Await)
    // fetch('http://localhost:2025/api/quotation/submit', {
    fetch('https://www.projectinfotech.in/api/quotation/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).catch(err => console.error("Background submission error:", err));

    // 2. Show Success Immediately (Fake 500ms delay for UX feel)
    setTimeout(() => {
        setIsSubmitting(false);
        setFormData({ name: '', companyName: '', phone: '', email: '', projectBrief: '' }); // Reset Form
        closeModal(); // Close Form Modal
        setShowSuccess(true); // Open Success Popup
    }, 500);
  };

  return (
    <div className="bg-white min-h-screen font-sans relative">
      
      {/* --- HERO SECTION --- */}
      <section className="relative bg-gradient-to-r from-red-900 to-red-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="container mx-auto px-4 py-20 md:py-28 relative z-10 flex flex-col-reverse md:flex-row items-center">
          <div className="md:w-1/2 mt-10 md:mt-0">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              Transform Your Business into a <span className="text-red-300">Digital Powerhouse</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-red-100 pr-8">
              Stop losing customers to competitors who are online. We build high-performance, secure custom websites that work 24/7 to bring you leads and build credibility.
            </p>
            <div className="flex gap-4">
              <button onClick={openModal} className="bg-white text-red-800 px-8 py-4 rounded-full font-bold text-lg hover:bg-red-100 transition shadow-xl flex items-center">
                Get Free Consultation <ArrowRight className="ml-2" size={20}/>
              </button>
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center">
            <img 
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Modern Website on Devices" 
              className="rounded-lg shadow-2xl transform md:rotate-3 hover:rotate-0 transition duration-500 border-4 border-white/20"
            />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg className="relative block w-full h-[100px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      {/* --- THE BUSINESS REALITY CHECK --- */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-10">Why a Professional Website is Non-Negotiable</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 bg-red-50 rounded-xl border border-red-100 text-center">
                <div className="w-20 h-20 mx-auto bg-red-200 rounded-full flex items-center justify-center mb-4">
                    <Clock size={40} className="text-red-700"/>
                </div>
                <h3 className="text-xl font-bold mb-2">The 24/7 Salesman</h3>
                <p className="text-gray-600">Your office closes, but your website never does. It informs and captures leads even while you sleep.</p>
            </div>
            <div className="p-6 bg-red-50 rounded-xl border border-red-100 text-center">
                <div className="w-20 h-20 mx-auto bg-red-200 rounded-full flex items-center justify-center mb-4">
                    <Shield size={40} className="text-red-700"/>
                </div>
                <h3 className="text-xl font-bold mb-2">Instant Credibility</h3>
                <p className="text-gray-600">Without a website, 80% of customers don't trust a business. A professional site validates your existence.</p>
            </div>
            <div className="p-6 bg-red-50 rounded-xl border border-red-100 text-center">
                <div className="w-20 h-20 mx-auto bg-red-200 rounded-full flex items-center justify-center mb-4">
                    <Globe size={40} className="text-red-700"/>
                </div>
                <h3 className="text-xl font-bold mb-2">Expand Beyond Local</h3>
                <p className="text-gray-600">Break geographical barriers. Attract clients from different cities or countries with SEO.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- OUR TECH STACK --- */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">We Use the Technology That Powers Giants</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We don't use outdated tools. We build your business on the same robust foundations used by Netflix, Amazon, and Google.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* MERN Stack Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group">
              <div className="h-48 bg-blue-900 flex items-center justify-center relative overflow-hidden">
                 <img src="https://images.unsplash.com/photo-1633356122544-f134324a6cee?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="React Node Visual" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-110 transition duration-500"/>
                 <div className="relative z-10 text-center">
                    <div className="flex justify-center gap-4 mb-2">
                        <Code size={40} className="text-blue-300" />
                        <Database size={40} className="text-green-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">MERN Full Stack</h3>
                 </div>
              </div>
              <div className="p-8">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  <strong className="text-blue-900">Best for:</strong> Startups, E-commerce, and dynamic Interactive Apps.
                  It's lightning fast and provides a smooth, app-like feeling on the web.
                </p>
                <ul className="space-y-3 bg-blue-50 p-4 rounded-lg">
                  <li className="flex items-center text-gray-700"><CheckCircle size={18} className="text-blue-600 mr-2"/> Single Page Applications (Fast)</li>
                  <li className="flex items-center text-gray-700"><CheckCircle size={18} className="text-blue-600 mr-2"/> Real-time updates</li>
                </ul>
              </div>
            </div>

            {/* Java Enterprise Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group">
              <div className="h-48 bg-red-800 flex items-center justify-center relative overflow-hidden">
                 <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Java Code Visual" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-110 transition duration-500"/>
                 <div className="relative z-10 text-center">
                    <Server size={50} className="text-red-300 mx-auto mb-2" />
                    <h3 className="text-2xl font-bold text-white">Java Enterprise</h3>
                 </div>
              </div>
              <div className="p-8">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  <strong className="text-red-900">Best for:</strong> Large Corporates, Banking, and High-Security systems.
                  When stability, security, and handling millions of users is priority#1.
                </p>
                 <ul className="space-y-3 bg-red-50 p-4 rounded-lg">
                  <li className="flex items-center text-gray-700"><CheckCircle size={18} className="text-red-600 mr-2"/> Bank-grade Security</li>
                  <li className="flex items-center text-gray-700"><CheckCircle size={18} className="text-red-600 mr-2"/> Extreme Scalability</li>
                </ul>
              </div>
            </div>

            {/* Python Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group">
              <div className="h-48 bg-yellow-700 flex items-center justify-center relative overflow-hidden">
                 <img src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Data Python Visual" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-110 transition duration-500"/>
                 <div className="relative z-10 text-center">
                    <div className="flex justify-center gap-2 mb-2">
                        <span className="text-4xl font-bold text-yellow-400">Py</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white">Python & Django</h3>
                 </div>
              </div>
              <div className="p-8">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  <strong className="text-yellow-900">Best for:</strong> Data-driven businesses, AI integration, and rapid launch.
                  If you need complex backends or AI features integrated easily.
                </p>
                 <ul className="space-y-3 bg-yellow-50 p-4 rounded-lg">
                  <li className="flex items-center text-gray-700"><CheckCircle size={18} className="text-yellow-600 mr-2"/> AI/ML Ready capabilities</li>
                  <li className="flex items-center text-gray-700"><CheckCircle size={18} className="text-yellow-600 mr-2"/> Secure & Versatile</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- OUR DEVELOPMENT PROCESS --- */}
       <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-12">Our Simple Path to Your Success</h2>
            <div className="flex flex-col md:flex-row justify-between items-center gap-8 relative">
                <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-red-100 -z-10 transform -translate-y-1/2"></div>

                <div className="bg-white p-6 rounded-full border-4 border-red-50 shadow-lg w-56 h-56 flex flex-col justify-center items-center relative z-10">
                    <div className="bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-3">1</div>
                    <h4 className="font-bold text-lg">Consult</h4>
                    <p className="text-sm text-gray-500 mt-1">We understand your business goals.</p>
                </div>
                <div className="bg-white p-6 rounded-full border-4 border-red-50 shadow-lg w-56 h-56 flex flex-col justify-center items-center relative z-10">
                    <div className="bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-3">2</div>
                    <h4 className="font-bold text-lg">Design</h4>
                    <p className="text-sm text-gray-500 mt-1">Creating visually stunning designs.</p>
                </div>
                <div className="bg-white p-6 rounded-full border-4 border-red-50 shadow-lg w-56 h-56 flex flex-col justify-center items-center relative z-10">
                    <div className="bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-3">3</div>
                    <h4 className="font-bold text-lg">Develop</h4>
                    <p className="text-sm text-gray-500 mt-1">Coding with robust technology.</p>
                </div>
                <div className="bg-white p-6 rounded-full border-4 border-red-100 shadow-xl w-56 h-56 flex flex-col justify-center items-center bg-red-50 relative z-10">
                    <div className="bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-3">4</div>
                    <h4 className="font-bold text-lg text-red-800">Launch</h4>
                    <p className="text-sm text-gray-600 mt-1">Deploying live to the world.</p>
                </div>
            </div>
        </div>
       </section>


      {/* --- CTA SECTION --- */}
      <section id="contact" className="relative py-20 px-4 text-center text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80" alt="Team working together" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-red-900 opacity-90"></div>
        </div>
        
        <div className="container mx-auto max-w-3xl relative z-10">
          <h2 className="text-4xl font-bold mb-6">Ready to dominate your market?</h2>
          <p className="text-xl text-red-100 mb-10">Don't let another customer go to your competitor. Let's discuss your project today.</p>
          <div className="flex flex-col md:flex-row justify-center gap-4">
            <a href="tel:+917782918158" className="bg-white text-red-900 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg flex items-center justify-center">
                Call Us Now
            </a>
            <button onClick={openModal} className="border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition">
                Request A Quote
            </button>
          </div>
        </div>
      </section>

      {/* --- QUOTATION MODAL FORM --- */}
      {isQuoteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-lg relative shadow-2xl animate-slide-up">
                <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 bg-gray-100 rounded-full p-1 transition">
                    <X size={24} />
                </button>
                
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Get A Free Quote</h3>
                <p className="text-gray-600 mb-6 text-sm">Tell us about your web project needs.</p>
                
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                        <input 
                            type="text" 
                            name="name" 
                            value={formData.name}
                            onChange={handleInputChange}
                            required 
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none" 
                            placeholder="John Doe"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name (Optional)</label>
                        <input 
                            type="text" 
                            name="companyName" 
                            value={formData.companyName}
                            onChange={handleInputChange}
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none" 
                            placeholder="Your Company Ltd."
                        />
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <input 
                                type="tel" 
                                name="phone" 
                                value={formData.phone}
                                onChange={handleInputChange}
                                required 
                                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none" 
                                placeholder="+91..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                            <input 
                                type="email" 
                                name="email" 
                                value={formData.email}
                                onChange={handleInputChange}
                                required 
                                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none" 
                                placeholder="john@example.com"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Project Brief / Idea</label>
                        <textarea 
                            name="projectBrief"
                            value={formData.projectBrief}
                            onChange={handleInputChange}
                            required 
                            rows={4} 
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none resize-none" 
                            placeholder="Tell us briefly what you want to build..."
                        ></textarea>
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full bg-red-600 text-white font-bold text-lg py-3 rounded-lg hover:bg-red-700 transition shadow-lg mt-4 disabled:bg-gray-400">
                        {isSubmitting ? 'Sending Request...' : 'Submit Request'}
                    </button>
                </form>
            </div>
        </div>
      )}

      {/* --- RENDER NEW SUCCESS POPUP --- */}
      <SuccessPopup isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </div>
  );
};

export default WithWeb;